import {
  type AskPunttAttachment,
  type AskPunttProjectData,
} from "@mg/schemas/src/christo/askPuntt";
import { CaretDown } from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { FileAttachmentButton } from "./FileAttachmentButton";
import { KeyFindingsSummarySection } from "./KeyFindingsSummarySection";
import { UsedConnectorsChips } from "./UsedConnectorsChips";

import { displayFileNameFromSource } from "../utils/projectContextDocuments";
import { type UsedConnector } from "../utils/usedConnectors";

function formatWorkflowLabel(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return "";
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
}

function attachmentDisplayName(att: AskPunttAttachment): string {
  const trimmed = att.name?.trim();
  if (trimmed) return trimmed;
  return (
    displayFileNameFromSource(att.source) ??
    displayFileNameFromSource(att.key) ??
    "File"
  );
}

function attachmentDownloadSource(att: AskPunttAttachment): string {
  if (att.source?.trim()) return att.source.trim();
  if (att.key?.trim()) return `https://static.puntt.ai/${att.key.trim()}`;
  return "";
}

/**
 * Right-hand "DETAILS" panel for multi-ticket Ask Puntt browse mode.
 * Mirrors the project workspace `CampaignDocsPanel` chrome: expandable section
 * with a read-only chat summary (old sidebar header), key findings, and
 * uploaded files from the chat transcript.
 */
export function ChatDetailsPanel({
  title,
  workflowType,
  description,
  findingsSummary,
  ticketFindings,
  uploadedFiles = [],
  usedConnectors = [],
  isKillerFlowStreaming = false,
  hasActiveStage = false,
  panelOpen: panelOpenProp,
  onPanelOpenChange,
}: {
  /** Chat title — mirrors the old multi-ticket sidebar header. */
  title: string;
  workflowType?: string;
  description?: string;
  findingsSummary?: string;
  ticketFindings?: AskPunttProjectData["ticketFindings"];
  /** User-uploaded files from chat message attachments. */
  uploadedFiles?: readonly AskPunttAttachment[];
  /** External connectors invoked anywhere in this chat. */
  usedConnectors?: readonly UsedConnector[];
  isKillerFlowStreaming?: boolean;
  hasActiveStage?: boolean;
  panelOpen?: boolean;
  onPanelOpenChange?: (open: boolean) => void;
}) {
  const { t } = useTranslation("ui");
  const [panelOpenInternal, setPanelOpenInternal] = useState(true);
  const panelOpen = panelOpenProp ?? panelOpenInternal;
  const setPanelOpen = (next: boolean) => {
    onPanelOpenChange?.(next);
    if (panelOpenProp === undefined) setPanelOpenInternal(next);
  };
  const [filesOpen, setFilesOpen] = useState(true);

  const workflowLabel = formatWorkflowLabel(workflowType ?? "");
  const descriptionText = description?.trim() ?? "";

  return (
    <section
      className={`flex min-h-0 flex-col overflow-hidden rounded-xl border border-puntt-neutral-gray-5 bg-base-white ${panelOpen ? "h-full flex-1" : "shrink-0"}`}
      aria-label={t("chatDetails")}
      data-testid="chat-details-panel"
      data-panel-expanded={panelOpen ? "true" : "false"}
    >
      <div className="flex shrink-0 items-center gap-1 border-b border-puntt-neutral-gray-5 px-4 py-3">
        <button
          type="button"
          onClick={() => setPanelOpen(!panelOpen)}
          aria-expanded={panelOpen}
          aria-label={t("toggleChatDetails")}
          data-testid="chat-details-toggle"
          className="flex min-w-0 flex-1 items-center gap-2 bg-base-white text-left"
        >
          <h2 className="flex-1 text-[11px] font-bold uppercase tracking-wide text-puntt-neutral-gray-12">
            {t("chatDetails")}
          </h2>
          <CaretDown
            size={14}
            aria-hidden="true"
            className={`shrink-0 text-puntt-neutral-gray-11 transition-transform ${panelOpen ? "" : "-rotate-90"}`}
          />
        </button>
      </div>

      {panelOpen && (
        <div
          className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto overscroll-contain p-4 [scrollbar-width:thin]"
          data-testid="chat-details-scroll"
        >
          <div data-testid="chat-details-summary">
            <h3
              className="text-[17px] font-bold leading-snug text-puntt-neutral-gray-12"
              data-testid="chat-details-title"
            >
              {title}
            </h3>
            {workflowLabel ? (
              <p
                className="mt-0.5 text-[13px] text-puntt-neutral-gray-9"
                data-testid="chat-details-workflow-type"
              >
                {workflowLabel}
              </p>
            ) : null}
            {descriptionText ? (
              <p
                className="mt-1.5 text-[13px] leading-relaxed text-puntt-neutral-gray-10"
                data-testid="chat-details-description"
              >
                {descriptionText}
              </p>
            ) : null}
          </div>

          <div data-testid="chat-details-key-findings-section">
            <KeyFindingsSummarySection
              findingsSummary={findingsSummary}
              ticketFindings={ticketFindings}
              isKillerFlowStreaming={isKillerFlowStreaming}
              hasActiveStage={hasActiveStage}
            />
          </div>

          <UsedConnectorsChips
            connectors={usedConnectors}
            testIdPrefix="chat-details"
          />

          <div data-testid="chat-details-uploaded-files-section">
            <button
              type="button"
              onClick={() => setFilesOpen((o) => !o)}
              aria-expanded={filesOpen}
              aria-label={t("toggleUploadedFiles")}
              data-testid="chat-details-uploaded-files-toggle"
              className="flex w-full items-center gap-2 rounded-md bg-base-white px-0 py-2 text-left"
            >
              <h3 className="flex-1 text-[13px] font-bold text-puntt-neutral-gray-12">
                {t("uploadedFiles")}
              </h3>
              <CaretDown
                size={14}
                aria-hidden="true"
                className={`shrink-0 text-puntt-neutral-gray-11 transition-transform ${filesOpen ? "" : "-rotate-90"}`}
              />
            </button>

            {filesOpen && (
              <div
                className="mt-2 flex flex-col gap-2"
                data-testid="chat-details-uploaded-files-body"
              >
                {uploadedFiles.length === 0 ? (
                  <p
                    className="text-[13px] leading-relaxed text-puntt-neutral-gray-11"
                    data-testid="chat-details-uploaded-files-empty"
                  >
                    {t("chatDetailsPanel.noFilesUploadedYet")}
                  </p>
                ) : (
                  uploadedFiles.map((att) => {
                    const name = attachmentDisplayName(att);
                    const source = attachmentDownloadSource(att);
                    if (!source) return null;
                    return (
                      <FileAttachmentButton
                        key={att.key || att.source}
                        url={source}
                        name={name}
                        testId={`chat-uploaded-file-${att.key || name}`}
                      />
                    );
                  })
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
